// Service pour la gestion des articles

/**
 * Une ligne de la grille tarifaire d'un article : le prix d'UN titulaire (identifie par son rang dans
 * l'accord-cadre) sur les trois tranches de quantite bornees par les seuils A et B de l'article.
 *
 * Un article en compte de 1 a 3, selon ce que le contexte autorise a voir :
 * - page Catalogue / suivi BC cote acheteur : tous les titulaires du couple lot technique x departement ;
 * - suivi BC cote prestataire : son seul rang (il ne voit jamais les prix de ses concurrents).
 *
 * Le rang n'est donc JAMAIS deduit : on affiche exactement les lignes que l'API a renvoyees.
 */
export interface PrixRang {
  rang: number;
  idLotContrat?: number;
  prixA: number; // quantite <= seuil A
  prixB: number; // seuil A < quantite <= seuil B
  prixC: number; // quantite > seuil B
  titulaire?: { nom?: string; siret?: string };
}

export interface Article {
  id: string;
  catalogueId?: string; // NOUVEAU: ID du catalogue pour la suppression
  code: string;
  nom: string;
  description: string;
  prix: any;
  unite: string;
  id_lot_technique?: number; // ✅ Ajouter id_lot_technique pour le formatage des prix
  quantite: number;
  totalHT: number;
  prixDegressifs?: {
    palier1: { min: number; max: number; prix: number };
    palier2: { min: number; max: number; prix: number };
    palier3: { min: number; prix: number };
  };
  // Seuils bruts du catalogue (bornes des tranches). `null` = non renseigne : affiche "—", et
  // `prixDegressifs` retombe alors sur ses defauts historiques (5 / 10).
  seuilA?: number | null;
  seuilB?: number | null;
  // Grille tarifaire complete, triee par rang croissant. Alimente le detail de l'article.
  prixRangs?: PrixRang[];
  detailsComplets?: {
    titre: string;
    descriptionLongue: string;
    prixParPrestataire: {
      [rang: string]: {
        palier1: string;
        palier2: string;
        palier3: string;
      };
    };
  };
}

// Service API pour les articles
export class ArticlesService {
  private static async fetchWithAuth(url: string, options: RequestInit = {}) {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Aucun token d\'authentification trouvé');
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Génère le numéro de référence formaté
   * @param departement Numéro du département
   * @param idLot ID du lot
   * @param code Code de l'article
   * @param rang Rang du prestataire
   * @returns Référence formatée
   */
  private static generateReference(departement: string | number, idLot: string | number, code: string, rang: string | number): string {
    return `${departement} ${idLot} ${code} ${rang}`;
  }

  /**
   * Récupère les informations du département depuis les commandes et l'article
   * @param commandes Array des commandes
   * @param articleItem Article actuel pour récupérer son id_lot_technique
   * @returns Informations du département et lot
   */
  private static extractDepartementInfo(commandes: any[], articleItem?: any): { departement: string; idLot: string } {

    if (commandes.length === 0) {
      return { departement: '00', idLot: '000' };
    }

    // Prendre les infos de la première commande
    const firstCommande = commandes[0];

    // CORRECTION: Essayer plusieurs chemins possibles pour extraire le département
    let departement = '00';
    let idLot = '000';

    // Essayer d'extraire le département
    if (firstCommande.operation?.lot_departement?.departement?.id) {
      departement = firstCommande.operation.lot_departement.departement.id.toString().padStart(2, '0');
    } else if (firstCommande.lot_departement?.departement?.id) {
      departement = firstCommande.lot_departement.departement.id.toString().padStart(2, '0');
    } else if (firstCommande.operation?.id_lot_departement) {
      departement = firstCommande.operation.id_lot_departement.toString().padStart(2, '0');
    } else if (firstCommande.id_lot_departement) {
      departement = firstCommande.id_lot_departement.toString().padStart(2, '0');
    } else {
    }

    // CORRECTION: Prioriser l'id_lot_technique de l'article, puis celui de l'opération
    if (articleItem && articleItem.id_lot_technique) {
      idLot = articleItem.id_lot_technique.toString();
    } else if (firstCommande.operation?.id_lot_technique) {
      idLot = firstCommande.operation.id_lot_technique.toString();
    } else if (firstCommande.id_lot_technique) {
      idLot = firstCommande.id_lot_technique.toString();
    } else if (firstCommande.operation?.lot_technique?.id) {
      idLot = firstCommande.operation.lot_technique.id.toString();
    } else if (firstCommande.lot_technique?.id) {
      idLot = firstCommande.lot_technique.id.toString();
    } else {
    }


    return { departement, idLot };
  }

  /**
   * Associe chaque `lot_contrat` visible a son titulaire (rang + entite), depuis les deux sources
   * possibles : la cle `titulaires` de la reponse (page Catalogue, ou seul le back connait les entites)
   * ou les commandes de l'operation (suivi BC, ou le payload porte deja contrat.entite).
   */
  private static mapTitulairesParLotContrat(
    titulaires: any[] | undefined,
    commandes: any[],
  ): Map<number, { rang?: number; nom?: string; siret?: string }> {
    const map = new Map<number, { rang?: number; nom?: string; siret?: string }>();

    (titulaires ?? []).forEach((t: any) => {
      const id = Number(t?.id_lot_contrat);
      if (!id) return;
      map.set(id, { rang: Number(t.rang) || undefined, nom: t.entite?.nom, siret: t.entite?.siret });
    });

    commandes.forEach((cmd: any) => {
      // `commande.id_contrat` porte en realite un `lot_contrat.id` (pivot lot geographique x contrat).
      const id = Number(cmd?.id_contrat ?? cmd?.contrat?.id);
      if (!id || map.has(id)) return;
      map.set(id, {
        rang: Number(cmd.rang) || undefined,
        nom: cmd.contrat?.entite?.nom,
        siret: cmd.contrat?.entite?.siret,
      });
    });

    return map;
  }

  /** Les lignes de prix triees par rang croissant, nommees quand le titulaire est connu. */
  private static buildPrixRangs(
    prix: any[] | undefined,
    titulaires: Map<number, { rang?: number; nom?: string; siret?: string }>,
  ): PrixRang[] {
    return (prix ?? [])
      .map((p: any): PrixRang => {
        const idLotContrat = Number(p.id_lot_contrat) || undefined;
        const titulaire = idLotContrat ? titulaires.get(idLotContrat) : undefined;
        return {
          rang: Number(p.rang) || titulaire?.rang || 0,
          idLotContrat,
          prixA: parseFloat(p.prix_a) || 0,
          prixB: parseFloat(p.prix_b) || 0,
          prixC: parseFloat(p.prix_c) || 0,
          titulaire: titulaire?.nom ? { nom: titulaire.nom, siret: titulaire.siret } : undefined,
        };
      })
      .sort((a, b) => (a.rang || 99) - (b.rang || 99));
  }

  /**
   * La ligne de prix du meilleur rang disponible. L'API ne garantit aucun ordre sur `item.prix` :
   * s'en remettre a `prix[0]` reviendrait a facturer un rang au hasard des que plusieurs titulaires
   * sont renvoyes (le cas de toute operation BC a 2 ou 3 commandes).
   */
  private static prixDuMeilleurRang(prix: any[] | undefined): any | null {
    if (!prix || prix.length === 0) return null;
    return [...prix].sort((a, b) => (Number(a.rang) || 99) - (Number(b.rang) || 99))[0];
  }

  /**
   * Récupère les articles du catalogue BC en utilisant les id_contrat des commandes
   * @param commandes Array des commandes contenant les id_contrat
   * @returns Promise<Article[]>
   */
  static async getArticlesByCommandes(commandes: any[]): Promise<Article[]> {
    try {

      if (!commandes || commandes.length === 0) {
        return [];
      }

      // Mode Catalogue acheteur : la commande synthetique porte `_catalogueLotQuery` (lot technique +
      // departement). On requete /bdc/catalogue/prix par (lot, dept) et le back resout le titulaire rang 1
      // -> un seul appel, pas de resolution separee. Sinon (suivi BC) : requete par id_contrat (existant).
      const lotQuery = commandes[0]?._catalogueLotQuery;
      let queryString: string;
      if (lotQuery && lotQuery.id_lot_technique && lotQuery.id_departement) {
        queryString = `id_lot_technique=${lotQuery.id_lot_technique}&id_departement=${lotQuery.id_departement}`;
      } else {
        // CORRECTION: Extraire les IDs de contrat en tenant compte des structures différentes
        const ids = commandes.map(cmd => {
          // Pour les prestataires transférés, le contrat peut être dans cmd directement
          if (cmd.id_contrat) {
            return cmd.id_contrat;
          }
          // Ou dans cmd.contrat.id
          if (cmd.contrat?.id) {
            return cmd.contrat.id;
          }
          // Pour les structures d'opération, essayer via l'opération
          if (cmd.operation && commandes.length === 1) {
            // Si c'est une opération transférée, utiliser les commandes de l'opération
            const operationCommandes = cmd.operation.commandes || [];
            return operationCommandes.map(opCmd => opCmd.id_contrat).filter(Boolean);
          }
          return null;
        }).flat().filter(Boolean);

        if (ids.length === 0) {
          return [];
        }

        queryString = `catalogue_prix_id_lot_contrat=${[...new Set(ids)].join(',')}`; // Dédupliquer
      }

      const response = await this.fetchWithAuth(`/bdc/catalogue/prix?${queryString}`);

      if (response.status !== 'success' || !response.data) {
        throw new Error('Format de réponse API inattendu');
      }

      const titulaires = this.mapTitulairesParLotContrat(response.titulaires, commandes);

      // CORRECTION: Adapter les données de l'API à la nouvelle structure avec références
      const articles: Article[] = response.data.data.map((item: any) => {

        // CORRECTION: Extraire les informations de département et lot POUR CHAQUE ARTICLE
        const { departement, idLot } = this.extractDepartementInfo(commandes, item);

        // Grille tarifaire de l'article : une ligne par titulaire visible dans ce contexte.
        const prixRangs = this.buildPrixRangs(item.prix, titulaires);

        // Prix de reference (celui qui pilote l'affichage et les totaux) = celui du MEILLEUR rang
        // renvoye. Cote acheteur c'est le rang 1 ; cote prestataire, son unique rang. Trier est
        // indispensable : l'API ne garantit aucun ordre sur `item.prix`.
        const prixData = this.prixDuMeilleurRang(item.prix);

        // CORRECTION: Générer la référence formatée
        const rang = prixData?.rang || '1';
        const reference = this.generateReference(departement, idLot, item.code, rang);


        // Calculer les prix dégressifs basés sur prix_a, prix_b, prix_c
        const prixDegressifs = prixData ? {
          palier1: {
            min: 1,
            max: item.seuil_a || 5,
            prix: parseFloat(prixData.prix_a) || 0
          },
          palier2: {
            min: (item.seuil_a || 5) + 1,
            max: item.seuil_b || 10,
            prix: parseFloat(prixData.prix_b) || 0
          },
          palier3: {
            min: (item.seuil_b || 10) + 1,
            prix: parseFloat(prixData.prix_c) || 0
          }
        } : undefined;

        const article = {
          id: item.id?.toString() || '',
          code: reference, // CORRECTION: Référence formatée
          nom: item.nom || '',
          description: item.description || '',
          prix: prixData,
          unite: item.unite || 'Unité',
          id_lot_technique: item.id_lot_technique, // ✅ AJOUTER pour le formatage des prix à 4 décimales
          quantite: 0,
          totalHT: 0,
          prixDegressifs,
          seuilA: item.seuil_a ?? null,
          seuilB: item.seuil_b ?? null,
          prixRangs
        };

        return article;
      });

      return articles;
    } catch (error) {
      console.error('Erreur lors de la récupération des articles du catalogue BC:', error);
      throw new Error('Impossible de récupérer les articles du catalogue');
    }
  }

  /**
   * Récupère les articles du catalogue BC (méthode de compatibilité)
   * @param lotIds IDs des lots de contrat
   * @returns Promise<Article[]>
   */
  static async getArticlesByLot(lotIds: string | number[]): Promise<Article[]> {
    try {
      console.warn('getArticlesByLot est obsolète pour BC, utilisez getArticlesByCommandes à la place');

      // Convertir en tableau si c'est un seul ID
      const ids = Array.isArray(lotIds) ? lotIds : [lotIds];
      const idsString = ids.join(',');

      const response = await this.fetchWithAuth(`/bdc/catalogue/prix?catalogue_prix_id_lot_contrat=${idsString}`);

      if (response.status !== 'success' || !response.data) {
        throw new Error('Format de réponse API inattendu');
      }

      // CORRECTION: Adapter les données avec références par défaut
      const articles: Article[] = response.data.data.map((item: any) => {
        // Trouver le prix correspondant au premier contrat disponible
        const prixData = item.prix && item.prix.length > 0 ? item.prix[0] : null;

        // CORRECTION: Générer référence par défaut (sans info complète de commande)
        const rang = prixData?.rang || '1';
        const reference = this.generateReference('00', item.id_lot_technique || '000', item.code, rang);

        // Calculer les prix dégressifs basés sur prix_a, prix_b, prix_c
        const prixDegressifs = prixData ? {
          palier1: {
            min: 1,
            max: item.seuil_a || 5,
            prix: parseFloat(prixData.prix_a) || 0
          },
          palier2: {
            min: (item.seuil_a || 5) + 1,
            max: item.seuil_b || 10,
            prix: parseFloat(prixData.prix_b) || 0
          },
          palier3: {
            min: (item.seuil_b || 10) + 1,
            prix: parseFloat(prixData.prix_c) || 0
          }
        } : undefined;

        return {
          id: item.id?.toString() || '',
          code: reference, // CORRECTION: Référence formatée
          nom: item.nom || '',
          description: item.description || '',
          prix: prixData ? parseFloat(prixData.prix_a) || 0 : 0,
          unite: item.unite || 'Unité',
          id_lot_technique: item.id_lot_technique, // ✅ AJOUTER pour le formatage des prix à 4 décimales
          quantite: 0,
          totalHT: 0,
          prixDegressifs
        };
      });

      return articles;
    } catch (error) {
      console.error('Erreur lors de la récupération des articles du catalogue BC:', error);
      throw new Error('Impossible de récupérer les articles du catalogue');
    }
  }

  /**
   * Récupère les détails complets d'un article
   * @param articleId ID de l'article
   * @returns Promise<Article>
   */
  // static async getArticleDetails(articleId: string): Promise<Article | null> {
  //   try {
  //     const response = await this.fetchWithAuth(`/bdc/catalogue/${articleId}`);

  //     if (response.status !== 'success' || !response.data) {
  //       return null;
  //     }

  //     const item = response.data;
  //     const prixData = item.prix && item.prix.length > 0 ? item.prix[0] : null;

  //     return {
  //       id: item.id?.toString() || articleId,
  //       code: item.code || '', // Ici on garde le code original pour les détails
  //       nom: item.nom || '',
  //       description: item.description || '',
  //       prix: prixData ? parseFloat(prixData.prix_a) || 0 : 0,
  //       unite: item.unite || 'Unité',
  //       quantite: 0,
  //       totalHT: 0,
  //       detailsComplets: {
  //         titre: item.nom || '',
  //         descriptionLongue: item.description || '',
  //         prixParPrestataire: {}
  //       }
  //     };
  //   } catch (error) {
  //     console.error('Erreur lors de la récupération des détails de l\'article:', error);
  //     return null;
  //   }
  // }

  /**
   * Calcule le prix selon la quantité et les paliers pour BC
   * @param article Article concerné
   * @param quantite Quantité commandée
   * @returns Prix unitaire selon la quantité
   */
  static getPrixByQuantity(article: Article, quantite: number): number {
    if (!article.prixDegressifs || quantite === 0) {
      return article.prix;
    }

    const { palier1, palier2, palier3 } = article.prixDegressifs;

    if (quantite >= palier1.min && quantite <= palier1.max) {
      return palier1.prix;
    } else if (quantite >= palier2.min && quantite <= palier2.max) {
      return palier2.prix;
    } else if (quantite >= palier3.min) {
      return palier3.prix;
    }

    return article.prix;
  }

  /**
   * Formate le label du palier de prix pour BC
   * @param palier Palier de prix
   * @returns Label formaté
   */
  static formatPalierLabel(palier: { min: number; max?: number }): string {
    if (palier.max && palier.max > 0) {
      return `${palier.min} à ${palier.max}`;
    }
    return `${palier.min}+`;
  }

  /**
   * Obtient le prix correspondant au rang du prestataire
   * @param item Article de l'API avec prix multiples
   * @param prestataireRang Rang du prestataire (1, 2, 3)
   * @returns Prix correspondant au rang
   */
  static getPrixByRang(item: any, prestataireRang: number): any {
    if (!item.prix || item.prix.length === 0) {
      return null;
    }

    // Chercher le prix correspondant au rang
    const prixForRang = item.prix.find((prix: any) => parseInt(prix.rang) === prestataireRang);

    // Si pas trouvé, utiliser le premier disponible
    return prixForRang || item.prix[0];
  }
}

export default ArticlesService;